import { ChatBubbleBottomCenterIcon } from '@heroicons/react/24/outline';
import env from '../../../environment';
import type { FeaturePageDataInterface } from './types';

export const onTopicResponses: FeaturePageDataInterface = {
  slug: 'on-topic-responses',
  navTitle: 'Structured, on-schema extraction',
  shortTitle: 'Structured data',
  navDescription:
    'Extract the fields you define — not free-text noise. Capture prospects and attributes that match your research schema.',
  Icon: ChatBubbleBottomCenterIcon,
  headline: 'Structured extraction,',
  highlight: 'grounded in your schema',
  blurb:
    'Unstructured scrapes waste analyst time. Stark Crawl extracts the fields you define — company details, contacts, product attributes, and other prospect signals — so research output is ready to use.',
  quote: {
    text: 'The schema is what won the team over. Every row lands in the same columns, so nobody is cleaning HTML dumps before analysis.',
    author: 'Customer name',
    authorRole: 'Job title, Company',
  },
  image: {
    src: '/images/shots/extraction-table.png',
    alt: 'Stark Crawl showing structured prospect rows filled against a research schema',
  },
  content: [
    {
      type: 'block',
      body: {
        heading: 'Fields you define, not page dumps',
        body: [
          'Generic scrapes return markup and noise. Stark Crawl extracts against a schema you control — company name, city, category, email, product attributes — so analysts get columns they can filter and join.',
          'If a page does not contain a field, the row stays honest about what is missing instead of inventing values.',
        ],
        large: true,
      },
    },
    {
      type: 'block',
      body: {
        heading: 'Comparable rows across hosts',
        body: [
          'The same schema applies across accepted hosts, so a prospect from a German directory and a French listing land in matching columns.',
          'That makes category coverage, competitor comparisons, and outreach lists practical without a separate normalisation step for every site.',
        ],
      },
    },
    {
      type: 'block',
      body: {
        heading: 'Research-ready enough for real work',
        body: [
          'Structured captures are what make crawls safe to put in front of analysts mapping a vertical, sales ops building a list, or researchers monitoring competitors.',
          'You stay in control of the source of truth: refine the schema, re-run, and export — nothing more, nothing less than the fields you asked for.',
        ],
      },
    },
    {
      type: 'solution',
      body: 'Define the fields that make a prospect useful and see structured rows fill in against your schema.',
      cta: 'Try for free',
      url: env.appUrl + '/register',
    },
  ],
  seo: {
    title: 'Structured data extraction for research - Stark Crawl',
    description:
      'Extract the fields you define from crawled pages — company details, contacts, and other prospect signals matched to your schema.',
    keywords:
      'structured data extraction, schema extraction, prospect fields, market research data, Stark Crawl',
  },
  ctaTitle: 'Rows you can analyse',
  ctaBody:
    'Extract the fields you define from crawled pages — structured, on-schema, and ready to export.',
};
